import { useCallback, useEffect, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { animate } from 'motion'
import { observer } from 'mobx-react-lite'
import * as THREE from 'three'
import {
    AnimationAction,
    AnimationClip,
    AnimationMixer,
    LoopOnce,
    LoopRepeat,
    NormalAnimationBlendMode,
} from 'three'
import type { HitAnimationType } from '@/src/stores/runStateTypes'
import {
    characterEndCardDuration,
    characterEndCardHeight,
    characterEndCardXOffset,
    characterEndCardYOffset,
    characterLevelCompleteResetDuration,
    laneTurnAngle,
    lanePositions,
    laneSwitchDuration,
    obstacleCollisionHideDelay,
    planePowerUpSpeedMultiplier,
    turnSmoothing,
    jumpHeight,
    jumpDuration,
    jumpVisualHeightMultiplier,
    jumpAnimationSpeedMultiplier,
    runAnimationSpeedMin,
    runAnimationSpeedMax,
} from '../../config/constants'
import { useRunState } from '../../provider/useRunState'
import { Character } from './Character'
import { useTrackController } from '../track/useTrackController'
import { store } from '@/src/stores/store'
import { PlayerHitboxDebug } from './PlayerHitboxDebug'
import { CAMERA_POSE_COMPLETED } from '../../camera/CameraAnimator'


// â”€â”€â”€ Feature Flags â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ENABLE_ADDITIVE_ANIMATIONS = false


// â”€â”€â”€ Animations-Konstanten â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const HIGH_FIVE_STAR_EFFECT_EARLY_END_SECONDS = 2
const END_CARD_REVEAL_ANIMATION_TIME_SCALE = 0.8
const END_CARD_REVEAL_ANIMATION_DAMPING = 6

const START_WAIT_ANIMATION = 'Anim_HighFive2'
const START_WAIT_ANIMATION_FALLBACK = 'Highfive2'
const START_CONFIRM_ANIMATION = 'Anim_HighFiveHit'
const START_CONFIRM_ANIMATION_FALLBACK = 'HighFiveHit'
const CONFIGURATOR_IDLE_ANIMATION = 'Anim_KonfigIdle'
const CONFIGURATOR_IDLE_ANIMATION_FALLBACK = 'KonfiguratorIdle'
const INTRO_THINK_ANIMATION = 'Anim_KonfigIdle'
const INTRO_THINK_ANIMATION_FALLBACK = 'IntroThink'
const WARM_UP_ANIMATION = 'Anim_WarmUp'
const WARM_UP_ANIMATION_FALLBACK = 'Old_WarmUp'
const GAME_OVER_LOOP_ANIMATION = 'Anim_GameOverLoop'
const WIN_LOOP_ANIMATION = 'Anim_WinLoop'
const GAME_OVER_TRANSITION_ANIMATION = 'Anim_Nosedive'
const RUN_FINISH_TRANSITION_ANIMATION = 'Anim_Run_finish'
const ADDITIVE_REFERENCE_ANIMATION = 'ReferencePose'

const HIGH_FIVE_STAR_EFFECT_START_EVENT = 'highfive-star-effect-start'
const HIGH_FIVE_STAR_EFFECT_END_EVENT = 'highfive-star-effect-end'

const ONCE_ANIMATIONS = new Set([
    'Anim_GetHit_Left',
    'Anim_GetHit_Right',
    'Anim_GetHalfHit_Left',
    'Anim_GetHalfHit_Right',
    'Anim_GameOver',
    'Anim_GameWin',
    WARM_UP_ANIMATION,
    WARM_UP_ANIMATION_FALLBACK,
    START_CONFIRM_ANIMATION,
    START_CONFIRM_ANIMATION_FALLBACK,
    GAME_OVER_TRANSITION_ANIMATION,
    RUN_FINISH_TRANSITION_ANIMATION,
])

const HIT_ANIMATION_NAMES = new Set([
    'Anim_GetHit_Left',
    'Anim_GetHit_Right',
    'Anim_GetHalfHit_Left',
    'Anim_GetHalfHit_Right',
])

const JUMP_ANIMATION_NAMES = new Set([
    'Anim_Jump',
    'Anim_Jump_Salto',
])

const hitAnimationClipByType: Record<HitAnimationType, string> = {
    lowerObstacleLeft: 'Anim_GetHalfHit_Left',
    lowerObstacleRight: 'Anim_GetHalfHit_Right',
    upperObstacleLeft: 'Anim_GetHit_Left',
    upperObstacleRight: 'Anim_GetHit_Right',
}

// Fallback-Idle-Kette: wird Ã¼berall als Ausweich-Animation genutzt
const IDLE_FALLBACK_NAMES = [
    CONFIGURATOR_IDLE_ANIMATION,
    CONFIGURATOR_IDLE_ANIMATION_FALLBACK,
    START_WAIT_ANIMATION,
    START_WAIT_ANIMATION_FALLBACK,
]

type MixerFinishedEvent = {
    type: 'finished'
    action: AnimationAction
    direction: number
}


// â”€â”€â”€ Hilfsfunktionen â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function startOnce(action: AnimationAction, timeScale = 1) {
    action.blendMode = NormalAnimationBlendMode
    action.enabled = true
    action.paused = false
    action.clampWhenFinished = true
    action.setLoop(LoopOnce, 1)
    action.setEffectiveWeight(1)
    action.setEffectiveTimeScale(timeScale)
    action.reset()
    action.fadeIn(0.15)
    action.play()
}

function startLoop(action: AnimationAction, timeScale = 1) {
    action.blendMode = NormalAnimationBlendMode
    action.enabled = true
    action.paused = false
    action.clampWhenFinished = false
    action.setLoop(LoopRepeat, Infinity)
    action.setEffectiveWeight(1)
    action.setEffectiveTimeScale(timeScale)
    action.reset()
    action.fadeIn(0.15)
    action.play()
}

function crossFadeTo(
    from: AnimationAction,
    to: AnimationAction,
    duration: number,
    loop: boolean,
    timeScale = 1,
) {
    to.blendMode = NormalAnimationBlendMode
    to.enabled = true
    to.paused = false
    to.clampWhenFinished = !loop
    to.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1)
    to.setEffectiveWeight(1)
    to.setEffectiveTimeScale(timeScale)
    to.reset()
    to.play()
    from.crossFadeTo(to, duration, false)
}

function stopAnimationAction(action: AnimationAction | null | undefined) {
    if (!action) return
    action.fadeOut(0.01)
    action.stop()
    action.reset()
    action.setEffectiveWeight(0)
}

function getFirstAvailableAction(
    actions: Record<string, AnimationAction | null>,
    names: string[],
): { name: string; action: AnimationAction } | null {
    for (const name of names) {
        const action = actions[name]
        if (action) return { name, action }
    }
    return null
}

function getFirstAvailableName(
    actions: Record<string, AnimationAction | null>,
    names: string[],
): string | null {
    return getFirstAvailableAction(actions, names)?.name ?? null
}

/**
 * Bereitet einen additiven Clip vor und gibt die fertige AnimationAction zurÃ¼ck.
 * Nutzt userData-Caching damit der Clip nur einmal verarbeitet wird.
 */
function prepareAdditiveAction(
    mixer: AnimationMixer,
    rawClip: AnimationClip,
    clips: AnimationClip[],
): AnimationAction {
    if (!rawClip.userData.additiveClipPrepared) {
        const referenceClip =
            clips.find(c => c.name === ADDITIVE_REFERENCE_ANIMATION) ??
            clips.find(c => c.name === CONFIGURATOR_IDLE_ANIMATION) ??
            clips.find(c => c.name === CONFIGURATOR_IDLE_ANIMATION_FALLBACK) ??
            clips[0]

        const sourceClip = rawClip.clone()
        const additiveClip = referenceClip
            ? THREE.AnimationUtils.makeClipAdditive(sourceClip, 0, referenceClip, 30)
            : THREE.AnimationUtils.makeClipAdditive(sourceClip)

        additiveClip.name = rawClip.name
        rawClip.userData.additiveClip = additiveClip
        rawClip.userData.additiveClipPrepared = true
    }

    const preparedClip = rawClip.userData.additiveClip as AnimationClip
    const action = mixer.clipAction(preparedClip, undefined, THREE.AdditiveAnimationBlendMode)
    action.enabled = true
    action.paused = false
    action.blendMode = THREE.AdditiveAnimationBlendMode
    action.setLoop(LoopRepeat, Infinity)
    action.setEffectiveTimeScale(1)
    return action
}


// â”€â”€â”€ Animations-Ziel bestimmen â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function getTargetAnimName(
    runState: ReturnType<typeof useRunState>,
    actions: Record<string, AnimationAction | null>,
): string | null {
    const phase = runState.phase

    if (phase === 'gameOver') {
        return getFirstAvailableName(actions, [GAME_OVER_LOOP_ANIMATION, 'Anim_GameOver'])
    }
    if (phase === 'levelComplete') return 'Anim_Run'
    if (phase === 'endCard') {
        return getFirstAvailableName(actions, [WIN_LOOP_ANIMATION, 'Anim_GameWin'])
    }

    const appPhase = store.appFlowPhase
    if (appPhase === 'countdown') {
        return getFirstAvailableName(actions, [WARM_UP_ANIMATION, WARM_UP_ANIMATION_FALLBACK])
    }
    if (appPhase === 'start' || appPhase === 'splash') {
        return getFirstAvailableName(actions, [START_WAIT_ANIMATION, START_WAIT_ANIMATION_FALLBACK])
    }
    if (appPhase === 'configurator') {
        if (store.configuratorActiveTab === 'effect') {
            return 'Anim_Run'
        }
        return getFirstAvailableName(actions, [CONFIGURATOR_IDLE_ANIMATION, CONFIGURATOR_IDLE_ANIMATION_FALLBACK])
    }
    if (appPhase === 'intro') {
        return getFirstAvailableName(actions, [INTRO_THINK_ANIMATION, INTRO_THINK_ANIMATION_FALLBACK])
    }

    const snapshot = runState.snapshot
    if (snapshot.hitReactionTimer > 0) {
        return snapshot.lastHitAnimation
            ? hitAnimationClipByType[snapshot.lastHitAnimation]
            : 'Anim_GetHit_Left'
    }

    if (snapshot.isJumping) return null
    if (snapshot.laneSwitchDirection === -1) return null
    if (snapshot.laneSwitchDirection === 1) return null

    if (phase === 'running' || phase === 'levelTransition') return 'Anim_Run'

    return getFirstAvailableName(actions, [START_WAIT_ANIMATION, START_WAIT_ANIMATION_FALLBACK])
}

function isEndSequencePhase(phase: string): boolean {
    return phase === 'gameOver' || phase === 'levelComplete' || phase === 'endCard'
}

function isLevelCompleteSequencePhase(phase: string): boolean {
    return phase === 'levelComplete' || phase === 'endCard'
}

function playTargetAnimation(
    actions: Record<string, AnimationAction | null>,
    targetName: string,
    fromAction?: AnimationAction | null,
) {
    const targetAction =
        actions[targetName] ??
        getFirstAvailableAction(actions, IDLE_FALLBACK_NAMES)?.action
    if (!targetAction) return

    if (ONCE_ANIMATIONS.has(targetName)) {
        startOnce(targetAction)
        return
    }

    if (fromAction && fromAction.getEffectiveWeight() > 0) {
        crossFadeTo(fromAction, targetAction, 0.15, true)
    } else {
        startLoop(targetAction)
    }
}


// â”€â”€â”€ Komponente â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const PlayerController = observer(function PlayerController() {
    const trackController = useTrackController()
    const runState = useRunState()
    const parentsCardLandingActive = store.parentsCardLandingActive
    const [playerVisible, setPlayerVisible] = useState(true)

    const playerGroup = useRef(new THREE.Group())

    const previousPhaseRef = useRef(runState.phase)
    const levelCompleteResetRef = useRef<ReturnType<typeof animate> | null>(null)
    const levelCompleteXRef = useRef(runState.snapshot.laneVisualX)
    const endCardAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const endCardHeightRef = useRef(0)

    const laneTurnAnimationRef = useRef(0)

    const jumpActiveRef = useRef(false)
    const handledJumpTriggerIdRef = useRef(runState.snapshot.jumpTriggerId)
    const jumpTimerRef = useRef(0)
    const jumpStartOffsetRef = useRef(0)
    const jumpOffsetRef = useRef(0)
    const jumpVisualOffsetRef = useRef(0)
    const jumpHitDropAnimationRef = useRef<ReturnType<typeof animate> | null>(null)
    const jumpDurationRef = useRef(jumpDuration / jumpAnimationSpeedMultiplier)

    const hitActiveRef = useRef(false)

    const actionsRef = useRef<Record<string, AnimationAction | null>>({})
    const mixerRef = useRef<AnimationMixer | null>(null)
    const runAnimationTimeScaleRef = useRef(1)
    const clipsRef = useRef<AnimationClip[]>([])
    const previousAnimNameRef = useRef<string | null>(null)
    const currentOverrideNameRef = useRef<string | null>(null)
    const overrideActiveRef = useRef(false)
    const pendingUnlockAnimationRef = useRef(false)
    const previousAppFlowPhaseRef = useRef(store.appFlowPhase)
    const finishedHandlerRef = useRef<((e: MixerFinishedEvent) => void) | null>(null)
    const highFiveStarEffectTimeoutRef = useRef<number | null>(null)
    const tailNoiseActionRef = useRef<AnimationAction | null>(null)
    const bendNoiseActionsRef = useRef<AnimationAction[]>([])



    // â”€â”€â”€ Hilfsmethoden â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const playOverride = useCallback((animName: string, timeScale = 1) => {
        const actions = actionsRef.current

        // PrimÃ¤re Action suchen, ggf. bekannte Fallbacks
        const resolvedOverride =
            actions[animName] != null
                ? { name: animName, action: actions[animName]! }
                : animName === START_CONFIRM_ANIMATION
                    ? getFirstAvailableAction(actions, [START_CONFIRM_ANIMATION_FALLBACK])
                    : null

        if (!resolvedOverride) return

        const { name: resolvedName, action: overrideAction } = resolvedOverride
        const fromAction =
            (previousAnimNameRef.current ? actions[previousAnimNameRef.current] : null) ??
            getFirstAvailableAction(actions, IDLE_FALLBACK_NAMES)?.action

        if (!fromAction) return

        currentOverrideNameRef.current = resolvedName
        overrideActiveRef.current = true

        // High-Five Star-Effekt
        if (
            resolvedName === START_CONFIRM_ANIMATION ||
            resolvedName === START_CONFIRM_ANIMATION_FALLBACK
        ) {
            if (highFiveStarEffectTimeoutRef.current != null) {
                window.clearTimeout(highFiveStarEffectTimeoutRef.current)
                highFiveStarEffectTimeoutRef.current = null
            }
            window.dispatchEvent(new Event(HIGH_FIVE_STAR_EFFECT_START_EVENT))

            const clipDurationSeconds = overrideAction.getClip().duration / Math.max(timeScale, 0.001)
            const earlyEndDelayMs = Math.max(
                0,
                (clipDurationSeconds - HIGH_FIVE_STAR_EFFECT_EARLY_END_SECONDS) * 1000,
            )
            highFiveStarEffectTimeoutRef.current = window.setTimeout(() => {
                window.dispatchEvent(new Event(HIGH_FIVE_STAR_EFFECT_END_EVENT))
                highFiveStarEffectTimeoutRef.current = null
            }, earlyEndDelayMs)
        }

        crossFadeTo(fromAction, overrideAction, 0.15, false, timeScale)
        previousAnimNameRef.current = resolvedName
    }, [])

    const resumeToTargetAnimation = useCallback((fromAction: AnimationAction) => {
        const actions = actionsRef.current
        const resumeAnimName = getTargetAnimName(runState, actions) ?? 'Anim_Run'
        const resumeAction =
            actions[resumeAnimName] ??
            actions['Anim_Run'] ??
            getFirstAvailableAction(actions, IDLE_FALLBACK_NAMES)?.action

        if (!resumeAction) return

        crossFadeTo(fromAction, resumeAction, 0.15, !ONCE_ANIMATIONS.has(resumeAnimName))
        previousAnimNameRef.current = resumeAnimName
    }, [runState])

    const interruptToCurrentTargetAnimation = useCallback(() => {
        const actions = actionsRef.current
        const transientActionNames = new Set<string>()

        JUMP_ANIMATION_NAMES.forEach((name) => transientActionNames.add(name))
        HIT_ANIMATION_NAMES.forEach((name) => transientActionNames.add(name))

        if (currentOverrideNameRef.current) {
            transientActionNames.add(currentOverrideNameRef.current)
        }

        if (
            previousAnimNameRef.current &&
            ONCE_ANIMATIONS.has(previousAnimNameRef.current)
        ) {
            transientActionNames.add(previousAnimNameRef.current)
        }

        transientActionNames.forEach((name) => {
            stopAnimationAction(actions[name])
        })

        jumpHitDropAnimationRef.current?.stop()
        jumpHitDropAnimationRef.current = null
        jumpActiveRef.current = false
        hitActiveRef.current = false
        overrideActiveRef.current = false
        currentOverrideNameRef.current = null
        jumpTimerRef.current = 0
        jumpStartOffsetRef.current = 0
        jumpOffsetRef.current = 0
        jumpVisualOffsetRef.current = 0
        runState.setJumpState({ isJumping: false, jumpTimer: 0, jumpOffset: 0 })

        if (highFiveStarEffectTimeoutRef.current != null) {
            window.clearTimeout(highFiveStarEffectTimeoutRef.current)
            highFiveStarEffectTimeoutRef.current = null
        }
        window.dispatchEvent(new Event(HIGH_FIVE_STAR_EFFECT_END_EVENT))

        const targetName =
            getTargetAnimName(runState, actions) ??
            getFirstAvailableName(actions, IDLE_FALLBACK_NAMES) ??
            'Anim_Run'
        playTargetAnimation(actions, targetName)
        previousAnimNameRef.current = targetName
    }, [runState])


    // â”€â”€â”€ Actions registrieren â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const registerActionsRef = useRef((
        actions: Record<string, AnimationAction | null>,
        mixer: AnimationMixer,
        clips: AnimationClip[],
    ) => {
        actionsRef.current = actions
        mixerRef.current = mixer
        runAnimationTimeScaleRef.current = mixer.timeScale
        clipsRef.current = clips

        if (finishedHandlerRef.current) {
            mixer.removeEventListener('finished', finishedHandlerRef.current)
        }

        // Additive Animations (TailNoise + Bend)
        if (ENABLE_ADDITIVE_ANIMATIONS) {
            const tailRawClip = clips.find(c => c.name === 'Add_TailNoise')
            if (tailRawClip) {
                const tailAction = prepareAdditiveAction(mixer, tailRawClip, clips)
                tailAction.setEffectiveWeight(1)
                tailAction.play()
                tailNoiseActionRef.current = tailAction
            }

            bendNoiseActionsRef.current = ['Add_BendLeft', 'Add_BendRight'].flatMap((clipName) => {
                const rawBendClip = clips.find(c => c.name === clipName)
                if (!rawBendClip) return []
                const bendAction = prepareAdditiveAction(mixer, rawBendClip, clips)
                bendAction.setEffectiveWeight(0)
                bendAction.play()
                return [bendAction]
            })
        }

        // Finished-Handler
        finishedHandlerRef.current = (e) => {
            const finishedAction = e.action
            const actions = actionsRef.current

            const isJumpAnim =
                finishedAction === actions['Anim_Jump'] ||
                finishedAction === actions['Anim_Jump_Salto']

            const isHitAnim =
                finishedAction === actions['Anim_GetHit_Left'] ||
                finishedAction === actions['Anim_GetHit_Right'] ||
                finishedAction === actions['Anim_GetHalfHit_Left'] ||
                finishedAction === actions['Anim_GetHalfHit_Right']

            const isCurrentOverride =
                currentOverrideNameRef.current != null &&
                finishedAction === actions[currentOverrideNameRef.current]

            if (isJumpAnim) {
                if (hitActiveRef.current || runState.snapshot.hitReactionTimer > 0) return

                currentOverrideNameRef.current = null
                overrideActiveRef.current = false
                resumeToTargetAnimation(finishedAction)
                return
            }

            if (isCurrentOverride) {
                // High-Five Timeout aufrÃ¤umen
                if (
                    currentOverrideNameRef.current === START_CONFIRM_ANIMATION ||
                    currentOverrideNameRef.current === START_CONFIRM_ANIMATION_FALLBACK
                ) {
                    if (highFiveStarEffectTimeoutRef.current != null) {
                        window.clearTimeout(highFiveStarEffectTimeoutRef.current)
                        highFiveStarEffectTimeoutRef.current = null
                    }
                }

                // Level-Complete Abschluss
                if (
                    currentOverrideNameRef.current === RUN_FINISH_TRANSITION_ANIMATION &&
                    runState.phase === 'levelComplete'
                ) {
                    currentOverrideNameRef.current = null
                    overrideActiveRef.current = false
                    runState.setLevelCompleteTimer(
                        Math.max(runState.snapshot.levelCompleteTimer, CAMERA_POSE_COMPLETED.transitionDuration),
                    )
                    runState.completeLevelComplete()
                    return
                }

                currentOverrideNameRef.current = null
                overrideActiveRef.current = false
                resumeToTargetAnimation(finishedAction)
                return
            }

            if (isHitAnim) {
                hitActiveRef.current = false
                overrideActiveRef.current = false
                runState.setHitReactionTimer(0)
                resumeToTargetAnimation(finishedAction)
            }
        }

        mixer.addEventListener('finished', finishedHandlerRef.current)
    })


    // â”€â”€â”€ Effects â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    useEffect(() => {
        const previousAppFlowPhase = previousAppFlowPhaseRef.current
        const currentAppFlowPhase = store.appFlowPhase
        previousAppFlowPhaseRef.current = currentAppFlowPhase

        const isRapidPreRunTransition =
            previousAppFlowPhase !== currentAppFlowPhase &&
            currentAppFlowPhase !== 'run' &&
            (overrideActiveRef.current ||
                currentOverrideNameRef.current != null ||
                ONCE_ANIMATIONS.has(previousAnimNameRef.current ?? ''))

        if (isRapidPreRunTransition) {
            interruptToCurrentTargetAnimation()
        }

        if (currentAppFlowPhase === 'start' || currentAppFlowPhase === 'splash') {
            setPlayerVisible(true)
        } else if (previousAppFlowPhase === 'start') {
            if (currentAppFlowPhase === 'configurator') {
                playOverride(START_CONFIRM_ANIMATION)
            } else {
                playOverride('Anim_GetHalfHit_Left')
            }
        }
    }, [interruptToCurrentTargetAnimation, playOverride, store.appFlowPhase])

    useEffect(() => {
        if (parentsCardLandingActive) setPlayerVisible(true)
    }, [parentsCardLandingActive])

    const upgradeSequenceFreezeRef = useRef(false)

    useEffect(() => {
        const handleStart = () => {
            upgradeSequenceFreezeRef.current = true
            trackController.setTrackPaused(true)
        }
        const handleEnd = () => {
            upgradeSequenceFreezeRef.current = false
            trackController.setTrackPaused(false)
        }
        window.addEventListener('upgrade-sequence-start', handleStart)
        window.addEventListener('upgrade-sequence-end', handleEnd)
        return () => {
            window.removeEventListener('upgrade-sequence-start', handleStart)
            window.removeEventListener('upgrade-sequence-end', handleEnd)
        }
    }, [trackController])

    useEffect(() => {
        return () => {
            if (highFiveStarEffectTimeoutRef.current != null) {
                window.clearTimeout(highFiveStarEffectTimeoutRef.current)
                highFiveStarEffectTimeoutRef.current = null
            }
            levelCompleteResetRef.current?.stop()
            levelCompleteResetRef.current = null
            endCardAnimationRef.current?.stop()
            endCardAnimationRef.current = null
            jumpHitDropAnimationRef.current?.stop()
            jumpHitDropAnimationRef.current = null
        }
    }, [])


    // â”€â”€â”€ Frame Loop â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    useFrame((_, deltaSeconds) => {
        const safeDelta = Math.min(deltaSeconds, 0.05)
        const snapshot = runState.snapshot
        const previousPhase = previousPhaseRef.current
        const currentPhase = runState.phase
        const actions = actionsRef.current
        const mixer = mixerRef.current
        const showConfiguratorEffectPreview =
            store.appFlowPhase === 'configurator' &&
            store.configuratorActiveTab === 'effect' &&
            store.footstepEffect !== ''

        // â”€â”€ Additive Animations sicherstellen â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (ENABLE_ADDITIVE_ANIMATIONS) {
            if (tailNoiseActionRef.current && !tailNoiseActionRef.current.isRunning()) {
                const tail = tailNoiseActionRef.current
                tail.enabled = true
                tail.paused = false
                tail.blendMode = THREE.AdditiveAnimationBlendMode
                tail.setLoop(LoopRepeat, Infinity)
                tail.setEffectiveWeight(1)
                tail.setEffectiveTimeScale(1)
                tail.play()
            }

            for (const bendAction of bendNoiseActionsRef.current) {
                if (!bendAction.isRunning()) {
                    bendAction.enabled = true
                    bendAction.paused = false
                    bendAction.blendMode = THREE.AdditiveAnimationBlendMode
                    bendAction.setLoop(LoopRepeat, Infinity)
                    bendAction.setEffectiveWeight(0)
                    bendAction.setEffectiveTimeScale(1)
                    bendAction.play()
                }
            }
        }

        // â”€â”€ Upgrade-Sequenz Freeze â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (upgradeSequenceFreezeRef.current) {
            if (mixer) mixer.timeScale = 0
            previousPhaseRef.current = currentPhase
            return
        }

        // â”€â”€ Hit wÃ¤hrend Jump: visuell landen â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (snapshot.hitReactionTimer > 0) {
            const interruptingJump =
                jumpActiveRef.current ||
                snapshot.isJumping ||
                JUMP_ANIMATION_NAMES.has(previousAnimNameRef.current ?? '')

            if (interruptingJump) {
                jumpHitDropAnimationRef.current?.stop()
                jumpHitDropAnimationRef.current = jumpVisualOffsetRef.current > 0
                    ? animate(jumpVisualOffsetRef.current, 0, {
                        duration: obstacleCollisionHideDelay,
                        ease: 'easeOut',
                        onUpdate: (latest) => { jumpVisualOffsetRef.current = latest },
                        onComplete: () => { jumpHitDropAnimationRef.current = null },
                    })
                    : null

                jumpActiveRef.current = false
                overrideActiveRef.current = false
                jumpTimerRef.current = 0
                jumpStartOffsetRef.current = 0
                jumpOffsetRef.current = 0
                jumpVisualOffsetRef.current = 0
                runState.setJumpState({ isJumping: false, jumpTimer: 0, jumpOffset: 0 })
            }
        }

        // â”€â”€ Animations-Speed â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (mixer) {
            if (currentPhase === 'paused') {
                mixer.timeScale = 0
                runAnimationTimeScaleRef.current = 0
            } else if (showConfiguratorEffectPreview) {
                mixer.timeScale = 1
                runAnimationTimeScaleRef.current = 1
            } else if (currentPhase === 'endCard') {
                const targetTimeScale = END_CARD_REVEAL_ANIMATION_TIME_SCALE
                const nextTimeScale = runState.levelCompleteCardAnimationComplete
                    ? targetTimeScale
                    : THREE.MathUtils.damp(
                        mixer.timeScale,
                        targetTimeScale,
                        END_CARD_REVEAL_ANIMATION_DAMPING,
                        safeDelta,
                    )
                mixer.timeScale = nextTimeScale
                runAnimationTimeScaleRef.current = nextTimeScale
            } else if (jumpActiveRef.current || overrideActiveRef.current) {
                mixer.timeScale = 1.0
                runAnimationTimeScaleRef.current = 1.0
            } else {
                const playback = trackController.getTrackPlaybackState()
                const speedProgress = THREE.MathUtils.clamp(
                    (playback.speed - playback.startSpeed) /
                    Math.max(playback.endSpeed - playback.startSpeed, 0.001),
                    0, 1,
                )
                mixer.timeScale = THREE.MathUtils.lerp(runAnimationSpeedMin, runAnimationSpeedMax, speedProgress)
                runAnimationTimeScaleRef.current = mixer.timeScale
            }
        }

        if (currentPhase === 'paused') {
            previousPhaseRef.current = currentPhase
            return
        }

        // â”€â”€ Phase Transitions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (isEndSequencePhase(previousPhase) && !isEndSequencePhase(currentPhase)) {
            stopAnimationAction(actions['Anim_Run'])

            if (previousPhase === 'gameOver') {
                stopAnimationAction(actions[GAME_OVER_TRANSITION_ANIMATION])
                stopAnimationAction(actions[GAME_OVER_LOOP_ANIMATION])
                stopAnimationAction(actions['Anim_GameOver'])
            }
            if (previousPhase === 'endCard') {
                stopAnimationAction(actions[WIN_LOOP_ANIMATION])
                stopAnimationAction(actions['Anim_GameWin'])
            }

            overrideActiveRef.current = false
            currentOverrideNameRef.current = null

            const prevAction = previousAnimNameRef.current
                ? actions[previousAnimNameRef.current]
                : null
            const targetName =
                getTargetAnimName(runState, actions) ??
                getFirstAvailableName(actions, IDLE_FALLBACK_NAMES) ??
                'Anim_Run'
            playTargetAnimation(actions, targetName, prevAction)
            previousAnimNameRef.current = targetName
        }

        if (previousPhase !== 'gameOver' && currentPhase === 'gameOver') {
            playOverride(GAME_OVER_TRANSITION_ANIMATION)
        }

        if (previousPhase !== 'levelComplete' && currentPhase === 'levelComplete') {
            playOverride(RUN_FINISH_TRANSITION_ANIMATION)
        }

        // â”€â”€ Reaktive Animations-Steuerung â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (!jumpActiveRef.current && !overrideActiveRef.current) {
            const targetName = getTargetAnimName(runState, actions)

            if (targetName && targetName !== previousAnimNameRef.current) {
                const targetAction = actions[targetName]
                const prevAction = previousAnimNameRef.current
                    ? actions[previousAnimNameRef.current]
                    : null
                playTargetAnimation(actions, targetName, prevAction)
                previousAnimNameRef.current = targetName

                if (targetAction) {
                    const isOnce = ONCE_ANIMATIONS.has(targetName)
                    const prevIsActive = prevAction != null && prevAction.getEffectiveWeight() > 0

                    if (isOnce) {
                        if (HIT_ANIMATION_NAMES.has(targetName)) {
                            if (hitActiveRef.current) return
                            hitActiveRef.current = true
                            overrideActiveRef.current = true
                        }
                        if (prevIsActive) {
                            crossFadeTo(prevAction!, targetAction, 0.15, false)
                        } else {
                            startOnce(targetAction)
                        }
                    } else {
                        if (prevIsActive) {
                            crossFadeTo(prevAction!, targetAction, 0.15, true)
                        } else {
                            startLoop(targetAction)
                        }
                    }
                    previousAnimNameRef.current = targetName
                }
            }
        }

        // â”€â”€ Level-complete / end-card Transitionen â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (isLevelCompleteSequencePhase(currentPhase) && !isLevelCompleteSequencePhase(previousPhase)) {
            levelCompleteResetRef.current?.stop()
            levelCompleteXRef.current = snapshot.laneVisualX
            levelCompleteResetRef.current = animate(levelCompleteXRef.current, 0, {
                duration: characterLevelCompleteResetDuration,
                ease: 'easeInOut',
                onUpdate: (latest) => {
                    levelCompleteXRef.current = latest
                    runState.setLaneVisualX(latest)
                },
            })
        }

        if (!isLevelCompleteSequencePhase(currentPhase) && isLevelCompleteSequencePhase(previousPhase)) {
            levelCompleteResetRef.current?.stop()
            levelCompleteResetRef.current = null
            levelCompleteXRef.current = snapshot.laneVisualX
        }

        if (currentPhase === 'endCard' && previousPhase !== 'endCard') {
            endCardAnimationRef.current = animate(0, characterEndCardHeight, {
                duration: characterEndCardDuration,
                ease: 'easeOut',
                onUpdate: (latest) => { endCardHeightRef.current = latest },
            })
        }

        if (currentPhase !== 'endCard' && previousPhase === 'endCard') {
            endCardAnimationRef.current?.stop()
            endCardAnimationRef.current = null
            endCardHeightRef.current = 0
        }

        previousPhaseRef.current = currentPhase

        // â”€â”€ Timers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (snapshot.hitFreezeTimer > 0) {
            runState.setHitFreezeTimer(Math.max(0, snapshot.hitFreezeTimer - safeDelta))
        }
        if (snapshot.hitReactionTimer > 0) {
            runState.setHitReactionTimer(Math.max(0, snapshot.hitReactionTimer - safeDelta))
        }

        // WÃ¤hrend HitFreeze: Position halten und frÃ¼h zurÃ¼ckkehren
        if (snapshot.hitFreezeTimer > 0) {
            runState.setCharacterVisualPosition([
                snapshot.characterVisualPosition[0],
                currentPhase === 'endCard' ? endCardHeightRef.current : jumpVisualOffsetRef.current,
                snapshot.characterVisualPosition[2],
            ])
            updatePlayerGroup(
                playerGroup.current,
                trackController,
                trackController.getTrackPlaybackState().trackPosition,
            )
            return
        }

        if (snapshot.planeTimer > 0) {
            runState.setPlaneTimer(Math.max(0, snapshot.planeTimer - safeDelta))
        }

        trackController.setSpeedMultiplier(
            snapshot.planeTimer > 0 ? planePowerUpSpeedMultiplier : 1.0
        )

        // â”€â”€ Lane Switch â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        let nextLaneX = snapshot.laneVisualX
        if (isLevelCompleteSequencePhase(currentPhase)) {
            nextLaneX = levelCompleteXRef.current
        } else {
            const targetLaneX = lanePositions[snapshot.laneIndex + 1]
            nextLaneX = THREE.MathUtils.damp(snapshot.laneVisualX, targetLaneX, laneSwitchDuration, safeDelta)
            runState.setLaneVisualX(nextLaneX)

            const laneOffset = targetLaneX - nextLaneX
            const direction = Math.sign(laneOffset) as -1 | 0 | 1
            if (direction === 0) {
                laneTurnAnimationRef.current = 0
            } else {
                const startLaneX = lanePositions[(snapshot.laneIndex - direction) + 1]
                const totalDistance = Math.abs(targetLaneX - startLaneX)
                const traveledDistance = Math.abs(nextLaneX - startLaneX)
                const rawT = totalDistance > 0 ? Math.min(traveledDistance / totalDistance, 1) : 1
                const curve = 4 * Math.pow(rawT, 0.8) * (1 - Math.pow(rawT, 0.8))
                const targetTurnRotation = -direction * laneTurnAngle * curve
                laneTurnAnimationRef.current = THREE.MathUtils.damp(
                    laneTurnAnimationRef.current, targetTurnRotation, turnSmoothing, safeDelta,
                )
            }
        }

        // â”€â”€ Jump â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (snapshot.jumpTriggerId !== handledJumpTriggerIdRef.current) {
            handledJumpTriggerIdRef.current = snapshot.jumpTriggerId
            jumpHitDropAnimationRef.current?.stop()
            jumpHitDropAnimationRef.current = null
            jumpStartOffsetRef.current = jumpActiveRef.current ? jumpOffsetRef.current : 0
            jumpActiveRef.current = true
            jumpTimerRef.current = 0
            currentOverrideNameRef.current = null
            overrideActiveRef.current = true

            const isSalto = snapshot.planeTimer > 0
            const jumpAnimName = isSalto ? 'Anim_Jump_Salto' : 'Anim_Jump'
            const jumpAction = actions[jumpAnimName]
            const fromAction = previousAnimNameRef.current
                ? actions[previousAnimNameRef.current]
                : actions['Anim_Run']

            if (jumpAction) {
                jumpDurationRef.current = jumpDuration
                jumpAction.clampWhenFinished = true
                jumpAction.setLoop(LoopOnce, 1)
                if (fromAction && fromAction !== jumpAction) {
                    crossFadeTo(fromAction, jumpAction, 0.15, false, jumpAnimationSpeedMultiplier)
                } else {
                    startOnce(jumpAction, jumpAnimationSpeedMultiplier)
                }
                previousAnimNameRef.current = jumpAnimName
            }
        }

        if (jumpActiveRef.current) {
            jumpTimerRef.current += safeDelta
            const progress = Math.min(jumpTimerRef.current / jumpDurationRef.current, 1)
            const isSalto = snapshot.planeTimer > 0
            const baseOffset = jumpStartOffsetRef.current * (1 - progress)
            const jumpAmplitude = Math.max(0, jumpHeight - jumpStartOffsetRef.current * 0.5)
            const jumpArc = Math.max(0, 1 - Math.pow(progress * 2 - 1, 2))
            const nextJumpOffset =
                progress >= 1
                    ? 0
                    : baseOffset + jumpArc * jumpAmplitude

            jumpOffsetRef.current = nextJumpOffset
            jumpVisualOffsetRef.current = isSalto ? 0 : nextJumpOffset * jumpVisualHeightMultiplier

            if (progress >= 1) {
                jumpActiveRef.current = false
                jumpTimerRef.current = 0
                jumpStartOffsetRef.current = 0
                jumpOffsetRef.current = 0
                jumpVisualOffsetRef.current = 0
                runState.setJumpState({ isJumping: false, jumpTimer: 0, jumpOffset: 0 })
                if (JUMP_ANIMATION_NAMES.has(previousAnimNameRef.current ?? '')) {
                    const finishedJumpAction = previousAnimNameRef.current
                        ? actions[previousAnimNameRef.current]
                        : null
                    if (finishedJumpAction) {
                        currentOverrideNameRef.current = null
                        overrideActiveRef.current = false
                        resumeToTargetAnimation(finishedJumpAction)
                    }
                }
            } else {
                runState.setJumpState({
                    isJumping: true,
                    jumpTimer: jumpTimerRef.current,
                    jumpOffset: jumpOffsetRef.current,
                })
            }
        }

        // â”€â”€ Position â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        const isEndCard = currentPhase === 'endCard'
        runState.setCharacterVisualPosition([
            isEndCard ? nextLaneX + characterEndCardXOffset : nextLaneX,
            isEndCard ? endCardHeightRef.current + characterEndCardYOffset : jumpVisualOffsetRef.current,
            snapshot.characterVisualPosition[2],
        ])

        updatePlayerGroup(
            playerGroup.current,
            trackController,
            trackController.getTrackPlaybackState().trackPosition,
        )
    })

    return (
        <group ref={playerGroup} visible={playerVisible}>
            <Character
                laneTurnAnimationRef={laneTurnAnimationRef}
                registerActionsRef={registerActionsRef}
                runAnimationTimeScaleRef={runAnimationTimeScaleRef}
            />
            <PlayerHitboxDebug />
        </group>
    )
})


// â”€â”€â”€ Hilfsfunktion: PlayerGroup positionieren â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function updatePlayerGroup(
    playerGroup: THREE.Group,
    trackController: ReturnType<typeof useTrackController>,
    trackPosition: number,
) {
    const frame = trackController.frameAtPosition(trackPosition)
    playerGroup.position.copy(frame.position)
    playerGroup.setRotationFromQuaternion(frame.cameraQuaternion)
}


